import * as React from 'react';
import { ChevronDown, ChevronUp, ListChecks } from 'lucide-react';
import { Card, HStack, VStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type {
  InfoItemDescriptor,
  LayoutOverrides,
} from '@components/shared/configurable';

/** Stable component id — must match the `register.ts` registration. */
const COMPONENT_ID = 'claimflow.otherActionsPanel';

/** Base (overridable) card title. */
const CARD_TITLE = 'Autres actions';

/**
 * One launcher row passed to the panel. `key` is the stable contract key
 * (matches `OtherActionDescriptor.key` / the page's `action-anchor-<key>`);
 * `label` is the display wording. Functions / data beyond this are not the
 * panel's concern — it only emits `onSelect(key)`.
 */
export interface OtherActionItem {
  key: string;
  label: string;
}

export interface OtherActionsPanelProps {
  /** Visible launcher rows, in mockup order (already filtered by the page). */
  actions: OtherActionItem[];
  /**
   * Fired with the chosen action's stable `key`. Optional to mirror the rest of
   * the configurable claimflow cards (callbacks are page-wired, never tunable),
   * which keeps the registry's `propsSchema` clean; the page always supplies it.
   */
  onSelect?: (key: string) => void;
  /**
   * Tenant-scoped layout overrides, injected by the `withComponentRules` HOC
   * (do not pass by hand). Only the `items` bucket (keyed by each action `key`)
   * and the component `title` are consumed; with no rules authored this is
   * `undefined` and the render is identical to the un-migrated panel.
   */
  _layoutOverrides?: LayoutOverrides;
}

/**
 * Sort comparator: ascending by `order`, with rows that have no `order` kept
 * *after* ordered ones in declaration order (stable). Mirrors `InfoList`'s
 * pipeline so reorder rules behave the same here as on the InfoList cards.
 */
function compareOrder(a: InfoItemDescriptor, b: InfoItemDescriptor): number {
  const ao = a.order ?? Number.POSITIVE_INFINITY;
  const bo = b.order ?? Number.POSITIVE_INFINITY;
  return ao - bo;
}

/**
 * "Autres actions" launcher — a collapsible card pinned at the bottom of the
 * task page's right column. Collapsed it shows a blue checklist icon + bold
 * title with a right-side chevron; expanded it lists one full-width,
 * left-aligned button per visible action. Clicking a button emits
 * `onSelect(key)` and recollapses the panel (the host then focuses or injects
 * the matching card).
 *
 * Configurable-ready: the rows render from the declarative `actions` config
 * (stable keys), tenant overrides are applied via `applyItemOverrides`, the
 * base list is published with `usePublishLayoutConfig` so the admin editor can
 * target it, and the title resolves through `resolveCardTitle`.
 */
export function OtherActionsPanel({
  actions,
  onSelect,
  _layoutOverrides,
}: Readonly<OtherActionsPanelProps>) {
  const [expanded, setExpanded] = React.useState(false);

  // Publish the base (pre-override) row list so the admin layout editor can
  // build a reliable target list (title + each action's key/label) — same
  // cheap, unconditional store write the InfoList cards do.
  usePublishLayoutConfig(
    COMPONENT_ID,
    buildPublishedConfigFromInfoItems(actions, CARD_TITLE),
  );

  // Apply tenant overrides (rename / hide / reorder) → drop hidden → stable-sort
  // by order. The rule engine folds info-item edits into the `fields` bucket, so
  // read `items` first then fall back to `fields` (mirrors `InfoList`).
  const visibleActions = React.useMemo(() => {
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    return applyItemOverrides<InfoItemDescriptor>(actions, overrides)
      .filter((item) => item.visible !== false)
      .slice()
      .sort(compareOrder);
  }, [actions, _layoutOverrides?.items, _layoutOverrides?.fields]);

  const toggle = React.useCallback(() => {
    setExpanded((open) => !open);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  const handleSelect = (key: string) => {
    onSelect?.(key);
    setExpanded(false);
  };

  return (
    <Card>
      {/* Header: icon + bold title on the left, chevron on the right. We drive
          the toggle from the header's own `onClick` (NOT Card.Header's built-in
          `collapsible`, whose chevron renders on the left) so the chevron sits
          on the right per the mockup. */}
      <Card.Header
        className="cursor-pointer select-none"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        action={
          <Icon
            icon={expanded ? ChevronUp : ChevronDown}
            size="md"
            color="secondary"
          />
        }
      >
        <HStack gap={2} align="center">
          <Icon icon={ListChecks} size="md" color="primary" />
          <Text as="span" weight="bold" size="lg">
            {resolveCardTitle(CARD_TITLE, _layoutOverrides)}
          </Text>
        </HStack>
      </Card.Header>
      {expanded && (
        <Card.Content>
          <VStack gap={3}>
            {visibleActions.map((item) => (
              <Button
                key={item.key}
                variant="ghost"
                className="w-full !justify-start border border-secondary-200 bg-secondary-50 transition-colors hover:border-secondary-300 hover:bg-secondary-100"
                onClick={() => {
                  handleSelect(item.key);
                }}
              >
                {item.label}
              </Button>
            ))}
          </VStack>
        </Card.Content>
      )}
    </Card>
  );
}
